import * as React from 'react';
import { ISpinnerProps } from './spinner-props';
import { isIndeterminate, isDeterminate } from '../progress-indicator/utils';
import {
    SpinnerWrapper, CircularSpinnerWrapper, CircularSpinner,
    CircularSpinnerFill, ProgressPercentage, getSpinnerSize
} from './spinner-parts';

interface ISpinnerState {
    canRenderComponent: boolean;
}

export class Spinner extends React.PureComponent<ISpinnerProps, ISpinnerState> {

    static defaultProps: Partial<ISpinnerProps> = {
        progress: 0,
        size: 'medium'
    };

    private timer?: number;

    constructor(props: ISpinnerProps) {
        super(props);
        const canRenderComponent = this.canRenderComponent();
        this.state = {
            canRenderComponent
        };
    }

    componentDidUpdate(prevProps: ISpinnerProps) {
        const { hidden, delay, spinnerType } = this.props;
        if (prevProps.hidden === hidden
            && prevProps.spinnerType === spinnerType
            && prevProps.delay === delay) {
            return;
        }
        if (!hidden && delay && isIndeterminate(spinnerType)) {
            this.showSpinnerAfterDelay();
            return;
        }
        this.updateViewState();
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }

    render() {
        const { size, className } = this.props;
        const { canRenderComponent } = this.state;
        return canRenderComponent
            ? (
                <SpinnerWrapper className={className} size={size}>
                    {this.renderCircularSpinner()}
                    {this.renderProgressPercentage()}
                </SpinnerWrapper>
            ) : null;
    }

    private renderCircularSpinner() {
        const { progress, spinnerType, size } = this.props;
        const currentProgress = isIndeterminate(spinnerType) ? 70 : progress!;
        const spinnerSize = getSpinnerSize(size);
        const circleSize = 2 * spinnerSize;
        const radius = spinnerSize - 5;
        const circumference = radius * Math.PI * 2;
        const viewBox = `0 0 ${circleSize} ${circleSize}`;
        const dashOffset = (circumference - (circumference * currentProgress / 105));
        return (
            <CircularSpinnerWrapper
                viewBox={viewBox}
                size={size}
                spinnerType={spinnerType}>
                <CircularSpinner
                    cx={spinnerSize}
                    cy={spinnerSize}
                    r={radius}
                    size={size} />
                <CircularSpinnerFill
                    cx={-spinnerSize}
                    cy={spinnerSize}
                    r={radius}
                    size={size}
                    progress={progress}
                    spinnerType={spinnerType}
                    circumference={circumference}
                    dashOffset={dashOffset} />
            </CircularSpinnerWrapper>
        );
    }

    private renderProgressPercentage() {
        const { progress, size, showPercentage, spinnerType } = this.props;

        return showPercentage && isDeterminate(spinnerType)
            ? (
                <ProgressPercentage size={size}>{progress}%</ProgressPercentage>
            ) : null;
    }

    private canRenderComponent() {
        const { hidden, delay, spinnerType } = this.props;
        let canRenderComponent = !hidden;
        if (canRenderComponent && delay && isIndeterminate(spinnerType)) {
            canRenderComponent = false;
            this.showSpinnerAfterDelay();
        }
        return canRenderComponent;
    }

    private showSpinnerAfterDelay() {
        const { delay } = this.props;
        clearTimeout(this.timer);
        this.timer = setTimeout(this.updateViewState, delay);
    }

    private updateViewState = () => {
        const { hidden } = this.props;
        this.setState({
            canRenderComponent: !hidden
        });
    }
}
